import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { getTeam } from '../api';

export default function AboutPage() {
  const { settings } = useOutletContext();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    getTeam().then(setTeam).catch(() => {});
  }, []);

  return (
    <div className="pt-28 pb-20">
      {/* Hero */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 mb-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">We Build Revenue Engines, Not Just Apps</h1>
          <p className="text-xl text-gray-600">
            Retic Media is a boutique development agency that measures success by one metric: the business outcomes we deliver for our clients.
          </p>
        </div>
      </div>

      {/* Methodology */}
      <div className="bg-gray-50 py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Approach: {settings.methodology_name || 'Revenue-Driven Development'}</h2>
            <p className="text-lg text-gray-600">
              Every agency says they're different. We prove it with a structured process that ties every design decision, every feature, and every line of code to measurable business results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '01', title: settings.methodology_step1_title || 'Diagnose', desc: settings.methodology_step1_desc || 'We audit your business model and tech stack.' },
              { step: '02', title: settings.methodology_step2_title || 'Build', desc: settings.methodology_step2_desc || 'We design with conversion in mind.' },
              { step: '03', title: settings.methodology_step3_title || 'Measure', desc: settings.methodology_step3_desc || 'We track real business outcomes.' },
            ].map(s => (
              <div key={s.step} className="bg-white p-8 rounded-xl shadow-sm text-center">
                <div className="text-4xl font-bold text-blue-100 mb-2">{s.step}</div>
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A tight-knit group of passionate developers, designers, and strategists. When you work with us, you work directly with the people building your product.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map(member => (
              <div key={member.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                {member.photo && (
                  <img src={member.photo} alt={member.name} className="w-full h-64 object-cover" />
                )}
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                  <p className="text-blue-600 mb-3">{member.role}</p>
                  {member.bio && <p className="text-gray-600 text-sm">{member.bio}</p>}
                  {member.linkedin_url && (
                    <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-gray-400 hover:text-blue-600 transition">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-blue-600 py-16 text-center text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Ready to Work With Us?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            We take on a limited number of clients each quarter to ensure dedicated attention. Let's see if we're a good fit.
          </p>
          <Link to="/growth-audit" className="bg-white text-blue-600 hover:bg-blue-50 py-3 px-8 rounded-full font-medium text-lg transition inline-block">
            Get Your Free Growth Audit
          </Link>
        </div>
      </div>
    </div>
  );
}
